import {
  createContext,
  useContext,
  useLayoutEffect,
  useRef,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { QRCodeSVG } from 'qrcode.react';

/**
 * The pieces a slide of this deck is composed from. They emit the Orca frame
 * vocabulary the other two Orca decks use (orca.css), so a slide here is typed
 * JSX rather than a generated HTML string.
 */

/** Which slide a frame is on: the corner mark prints it. Set in slides.tsx. */
export type Page = { n: number; total: number; section: string };
export const PageContext = createContext<Page>({ n: 0, total: 0, section: '' });

const two = (n: number) => String(n).padStart(2, '0');

/** The Orca wordmark and whale (orca-website/src/components/orca-logo.tsx). */
export function OrcaLogo() {
  return (
    <svg viewBox="0 0 112.583 21" fill="none" role="img" aria-label="Orca">
      <path
        d="M30.3665 1.3125C27.5885 1.3125 25.039 2.25049 23.0326 3.81982C21.8535 4.73977 20.8596 5.87619 20.1188 7.16893C19.378 5.87619 18.3841 4.73977 17.205 3.81982C15.2049 2.25049 12.6553 1.3125 9.8773 1.3125H0C0 6.53759 4.33984 10.7646 9.70445 10.7646H9.8773C14.9518 10.7646 19.057 14.751 19.0755 19.6875H21.1745C21.193 14.751 25.2982 10.7646 30.3727 10.7646H30.5456C35.9102 10.7646 40.25 6.53759 40.25 1.3125H30.3727L30.3665 1.3125Z"
        fill="#e2ffa5"
      />
      <path
        d="M56.7432 21C54.6113 21 52.7393 20.5719 51.1273 19.7158C49.5326 18.8429 48.2933 17.6175 47.4093 16.0396C46.5253 14.4616 46.0833 12.6151 46.0833 10.5C46.0833 8.36811 46.5253 6.52158 47.4093 4.96043C48.2933 3.38249 49.5326 2.16547 51.1273 1.30935C52.7219 0.43645 54.5939 0 56.7432 0C58.8926 0 60.7645 0.428057 62.3592 1.28417C63.9712 2.14029 65.2105 3.35731 66.0772 4.93525C66.9612 6.4964 67.4032 8.35132 67.4032 10.5C67.4032 12.6151 66.9612 14.4616 66.0772 16.0396C65.1932 17.6175 63.9538 18.8429 62.3592 19.7158C60.7645 20.5719 58.8926 21 56.7432 21ZM56.7432 17.9532C58.9446 17.9532 60.6605 17.2818 61.8912 15.9389C63.1392 14.5959 63.7632 12.783 63.7632 10.5C63.7632 8.20024 63.1479 6.38729 61.9172 5.06115C60.6865 3.71822 58.9619 3.04676 56.7432 3.04676C54.5419 3.04676 52.8173 3.71822 51.5693 5.06115C50.3386 6.38729 49.7233 8.20024 49.7233 10.5C49.7233 12.7998 50.3386 14.6211 51.5693 15.964C52.8173 17.2902 54.5419 17.9532 56.7432 17.9532Z"
        fill="#f8fafc"
      />
      <path
        d="M70.8331 20.6978V9.03957H69.0391V5.58993H72.7311V8.48561H73.8231C74.2391 7.4952 74.8977 6.73981 75.7991 6.21942C76.7177 5.69904 77.7664 5.43885 78.945 5.43885H80.245V8.38489H78.789C77.3157 8.38489 76.1977 8.79616 75.4351 9.6187C74.6897 10.4412 74.3171 11.5408 74.3171 12.9173V20.6978H70.8331Z"
        fill="#f8fafc"
      />
      <path
        d="M88.1863 21C86.5917 21 85.1964 20.6811 84.0004 20.0432C82.8044 19.3885 81.8684 18.4736 81.1924 17.2986C80.5337 16.1235 80.2044 14.7386 80.2044 13.1439C80.2044 11.5492 80.5337 10.1643 81.1924 8.98921C81.851 7.81415 82.7784 6.90767 83.9744 6.26978C85.1704 5.61511 86.5743 5.28777 88.1863 5.28777C90.1623 5.28777 91.8003 5.76619 93.1003 6.72302C94.4003 7.67986 95.2409 9.02278 95.6223 10.7518H92.0603C91.8003 9.92926 91.341 9.30815 90.6823 8.88849C90.041 8.46882 89.209 8.25899 88.1863 8.25899C86.817 8.25899 85.7337 8.68705 84.9364 9.54317C84.139 10.3993 83.7404 11.5995 83.7404 13.1439C83.7404 14.6882 84.139 15.8885 84.9364 16.7446C85.7337 17.6007 86.817 18.0288 88.1863 18.0288C90.3356 18.0288 91.653 17.1139 92.1383 15.2842H95.7003C95.3536 17.1139 94.5303 18.524 93.2303 19.5144C91.9303 20.5048 90.249 21 88.1863 21Z"
        fill="#f8fafc"
      />
      <path
        d="M102.313 20.9748C100.528 20.9748 99.1154 20.5468 98.0755 19.6906C97.0528 18.8345 96.5415 17.7098 96.5415 16.3165C96.5415 14.8561 97.0615 13.723 98.1015 12.9173C99.1414 12.1115 100.632 11.7086 102.573 11.7086H107.513V11.0791C107.513 9.06475 106.335 8.05755 103.977 8.05755C101.984 8.05755 100.788 8.7542 100.389 10.1475H96.8535C97.1481 8.58633 97.9108 7.38609 99.1414 6.54676C100.389 5.70744 102.01 5.28777 104.003 5.28777C106.239 5.28777 107.947 5.79137 109.125 6.79856C110.304 7.78897 110.893 9.23261 110.893 11.1295V17.2482H112.583V20.6978H108.917V18.3058H107.825C107.288 19.1451 106.56 19.7998 105.641 20.2698C104.723 20.7398 103.613 20.9748 102.313 20.9748ZM102.833 18.3058C103.717 18.3058 104.515 18.1379 105.225 17.8022C105.936 17.4664 106.491 17.0216 106.889 16.4676C107.305 15.8969 107.513 15.2674 107.513 14.5791V14.1007H102.755C100.849 14.1007 99.8954 14.789 99.8954 16.1655C99.8954 16.8369 100.155 17.3657 100.675 17.7518C101.195 18.1211 101.915 18.3058 102.833 18.3058Z"
        fill="#f8fafc"
      />
    </svg>
  );
}

/** Logo, rule, domain: the head of the title and closing frames. */
export function Lockup() {
  return (
    <div className="lockup">
      <OrcaLogo />
      <span className="rule" />
      <span className="domain">RUNORCA.AI</span>
    </div>
  );
}

/** The frame's authored size. orca.css scales it to its stage by `--fit`. */
const FRAME_WIDTH = 1280;
const FRAME_HEIGHT = 720;

/**
 * Measures a stage and publishes how far its frame has to scale to fit it.
 * CSS could work this out (100cqw / 1280px), but only where typed division
 * exists, and a public link is opened in every browser.
 */
function useFit() {
  const stage = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = stage.current;
    if (!el) return;
    const fit = () => {
      if (!el.clientWidth || !el.clientHeight) return;
      const scale = Math.min(el.clientWidth / FRAME_WIDTH, el.clientHeight / FRAME_HEIGHT);
      el.style.setProperty('--fit', String(scale));
    };
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return stage;
}

/**
 * One 1280x720 frame. `deep` leads a part; `opener` is the title and closing
 * treatment, with the wave and without the footer furniture.
 */
export function Frame({
  variant = 'plain',
  children,
}: {
  variant?: 'plain' | 'deep' | 'opener';
  children: ReactNode;
}) {
  const page = useContext(PageContext);
  const stage = useFit();
  const opener = variant === 'opener';
  return (
    <div className="orca-stage" ref={stage}>
      <section className={variant === 'plain' ? 'frame' : `frame ${variant}`}>
        {opener && (
          <div className="wave">
            <img src="/orca-aiconf/ocean.webp" alt="" />
          </div>
        )}
        <div className="layer">{children}</div>
        {!opener && (
          <div className="mark">
            <OrcaLogo />
            <span>
              {page.section} · {two(page.n)} / {two(page.total)}
            </span>
          </div>
        )}
      </section>
    </div>
  );
}

/** The lime part of a statement. */
export function Hi({ children }: { children: ReactNode }) {
  return <span className="hi">{children}</span>;
}

/**
 * Eyebrow and statement, the head of every content frame. `lab` ties the slide
 * to the tutorial lab where the room does it by hand.
 */
export function Head({
  eyebrow,
  lab,
  sub,
  children,
}: {
  eyebrow: ReactNode;
  lab?: string;
  sub?: ReactNode;
  children: ReactNode;
}) {
  return (
    <header>
      <p className="eyebrow">{eyebrow}</p>
      {lab && <span className="ctag">{lab}</span>}
      <h2 className="statement">{children}</h2>
      {sub && (
        <p className="sub" style={{ marginTop: 8 }}>
          {sub}
        </p>
      )}
    </header>
  );
}

/** A website diagram, shown unmodified: the file embeds its own font. */
export function Figure({ src, alt, style }: { src: string; alt: string; style?: CSSProperties }) {
  return (
    <div className="fig" style={style}>
      <img src={src} alt={alt} />
    </div>
  );
}

/** An inline diagram from diagrams.tsx, sized by the space the frame has left. */
export function Diagram({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <div className="fig" style={style}>
      {children}
    </div>
  );
}

/* ── code ────────────────────────────────────────────────────────────── */

export type Lang = 'bash' | 'python' | 'ts' | 'json';

const STRING = /"(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*'/g;

/** A comment after a command, set off from it by two spaces or more. */
const TAIL = /\s{2,}#\s.*$/;

/**
 * Just enough colour to read a sample from the back of a room: comments, and
 * strings. In JSON a string that is a key keeps the body colour, so keys and
 * values tell apart. No highlighter dependency.
 */
function tokens(line: string, lang: Lang): ReactNode {
  if (lang !== 'json' && /^\s*(#|\/\/)/.test(line)) {
    return <span className="t-cm">{line}</span>;
  }
  const tail = lang === 'bash' || lang === 'python' ? TAIL.exec(line) : null;
  const code = tail ? line.slice(0, tail.index) : line;
  const out: ReactNode[] = [];
  let last = 0;
  for (const match of code.matchAll(STRING)) {
    const start = match.index ?? 0;
    const end = start + match[0].length;
    if (start > last) out.push(code.slice(last, start));
    const isKey = lang === 'json' && /^\s*:/.test(code.slice(end));
    out.push(
      isKey ? (
        match[0]
      ) : (
        <span key={start} className="t-str">
          {match[0]}
        </span>
      ),
    );
    last = end;
  }
  if (last < code.length) out.push(code.slice(last));
  if (tail) {
    out.push(
      <span key="tail" className="t-cm">
        {tail[0]}
      </span>,
    );
  }
  return out;
}

/**
 * A code sample. `hl` and `dim` take 1-based line numbers. Sizes: `lg` (16px)
 * for a full-width block, `md` (15px) for two columns, `sm` (14px) for three,
 * `xl` (18px) for a short sample with room to spare.
 */
export function Code({
  lang,
  code,
  label,
  hl = [],
  dim = [],
  size = 'lg',
  style,
}: {
  lang: Lang;
  code: string;
  label?: ReactNode;
  hl?: readonly number[];
  dim?: readonly number[];
  size?: 'xl' | 'lg' | 'md' | 'sm';
  style?: CSSProperties;
}) {
  const lines = code.split('\n');
  return (
    <div className="codebox" style={style}>
      {label && <div className="code-label">{label}</div>}
      <pre className={size === 'lg' ? 'code' : `code ${size}`}>
        {lines.map((line, i) => {
          const n = i + 1;
          const state = hl.includes(n) ? ' hl' : dim.includes(n) ? ' dim' : '';
          return (
            <span key={n} className={`ln${state}`}>
              {tokens(line, lang)}
            </span>
          );
        })}
      </pre>
    </div>
  );
}

/* ── lists ───────────────────────────────────────────────────────────── */

export type Step = { t: ReactNode; d?: ReactNode };

/** Numbered steps. `start` continues a count across two slides. */
export function Steps({ items, start = 1 }: { items: readonly Step[]; start?: number }) {
  return (
    <ol className="steps">
      {items.map((item, i) => (
        <li key={i}>
          <span className="num">{start + i}</span>
          <div>
            <div className="t">{item.t}</div>
            {item.d && <div className="d">{item.d}</div>}
          </div>
        </li>
      ))}
    </ol>
  );
}

export type LogLine = { type: string; text: string };

/** A session's event log: the type as the API names it, and an example value. */
export function EventLog({
  title = 'Event log',
  lines,
  large = false,
}: {
  title?: string;
  lines: readonly LogLine[];
  large?: boolean;
}) {
  return (
    <div className={large ? 'evlog lg' : 'evlog'}>
      <div className="head">{title}</div>
      {lines.map(line => (
        <div key={`${line.type}:${line.text}`} className="row">
          <span className="ty">{line.type}</span>
          <span className="tx">{line.text}</span>
        </div>
      ))}
    </div>
  );
}

/** A panel: a mono label, an optional display name, a body. */
export function Panel({
  label,
  name,
  fine,
  children,
}: {
  label: ReactNode;
  name?: ReactNode;
  fine?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="panel">
      <h3>{label}</h3>
      {name && <div className="name">{name}</div>}
      <p>{children}</p>
      {fine && <p className="fine">{fine}</p>}
    </div>
  );
}

/**
 * A table: a mono header row, a hairline under each row. `cols` is the grid's
 * column template, so each table sizes its own columns. `dense` is for a
 * table that shares its slide with something else.
 */
export function Table({
  cols,
  head,
  rows,
  dense = false,
}: {
  cols: string;
  head: readonly ReactNode[];
  rows: readonly (readonly ReactNode[])[];
  dense?: boolean;
}) {
  return (
    <div className={dense ? 'tbl dense' : 'tbl'} role="table" style={{ gridTemplateColumns: cols }}>
      <div className="tr" role="row">
        {head.map((cell, i) => (
          <div key={i} className="th" role="columnheader">
            {cell}
          </div>
        ))}
      </div>
      {rows.map((row, r) => (
        <div key={r} className="tr" role="row">
          {row.map((cell, c) => (
            <div key={c} className="td" role="cell">
              {cell}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

/** One request in a chain of calls: what you send, and what comes back. */
export function Call({
  n,
  name,
  endpoint,
  send,
  get,
  children,
}: {
  n: number;
  name: string;
  endpoint: readonly string[];
  send: ReactNode;
  get: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="panel call">
      <div className="call-head">
        <span className="num">{n}</span>
        <span className="name">{name}</span>
      </div>
      <div className="ep">
        {endpoint.map(line => (
          <div key={line}>{line}</div>
        ))}
      </div>
      <p>{children}</p>
      <dl className="io">
        <dt>You send</dt>
        <dd>{send}</dd>
        <dt>You get</dt>
        <dd className="get">{get}</dd>
      </dl>
    </div>
  );
}

/* ── part 03: resource by resource ───────────────────────────────────── */

/** The nine resources, named as the resource model diagram names them. */
export const RESOURCES = [
  'Agent',
  'Environment',
  'Session',
  'Events',
  'Vault',
  'File',
  'MemoryStore',
  'Skill',
  'Trigger',
] as const;
export type Resource = (typeof RESOURCES)[number];

/**
 * Where a slide sits among the nine resources: the eyebrow of every slide in
 * part 03. `detail` names the part of the resource a slide is about.
 */
export function ResourceNav({ at, detail }: { at: Resource; detail?: string }) {
  return (
    <span className="rnav">
      <span className="part">03</span>
      {RESOURCES.map(name => (
        <span key={name} className={name === at ? 'on' : undefined} aria-current={name === at || undefined}>
          {name === at && detail ? `${name} · ${detail}` : name}
        </span>
      ))}
    </span>
  );
}

/** The foot of a resource slide: where it lives in the API, and in the CLI. */
export function ApiLine({ api, cli }: { api: ReactNode; cli: ReactNode }) {
  return (
    <div className="apiline">
      <span className="k">API</span>
      <span className="v">{api}</span>
      <span className="k">CLI</span>
      <span className="v">{cli}</span>
    </div>
  );
}

/**
 * A QR code on a light tile, with what it opens under it. Sized to be scanned
 * from a seat, and drawn with the four-module quiet zone the standard asks for.
 */
export function Qr({ url, title, lines }: { url: string; title: string; lines: readonly string[] }) {
  return (
    <div className="qrcard scrim">
      <div className="qr">
        <QRCodeSVG value={url} size={236} level="M" marginSize={4} bgColor="#f8fafc" fgColor="#12272a" />
      </div>
      <div className="big">{title}</div>
      <div className="small">
        {lines.map(line => (
          <div key={line}>{line}</div>
        ))}
      </div>
    </div>
  );
}
